import { useCallback } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useAuth, useStore, StoreActions } from 'providers';
import {
  Customer,
  OnboardedStatus,
  LoyaltyData,
  VoucherList,
  Offers,
  CarouselOffers,
  SurveyQuestions,
  ReferralCode,
  Transactions,
} from 'models';

import {
  getUserData,
  getLoyaltyData,
  getOffersData,
  getVouchersData,
  getOnboardedStatus,
  getCarouselOffersData,
  getSurveyData,
  getReferralCode,
  getTransactionData,
} from 'services/loyaltyGateway';

type EndpointTypes =
  | OnboardedStatus
  | Customer
  | VoucherList
  | Offers
  | LoyaltyData
  | CarouselOffers
  | SurveyQuestions
  | ReferralCode
  | Transactions;

interface Actions {
  endpoint: () => Promise<EndpointTypes>;
  action: StoreActions;
}

type Endpoints =
  | 'onboardedStatus'
  | 'userData'
  | 'vouchersData'
  | 'loyaltyData'
  | 'offersData'
  | 'offersCarouselData'
  | 'surveyQuestions'
  | 'referralCode'
  | 'transactions';

const services: Record<Endpoints, Actions> = {
  onboardedStatus: {
    endpoint: getOnboardedStatus,
    action: 'setOnboardedStatus',
  },
  userData: {
    endpoint: getUserData,
    action: 'setUserData',
  },
  vouchersData: {
    endpoint: getVouchersData,
    action: 'setVouchersData',
  },
  loyaltyData: {
    endpoint: getLoyaltyData,
    action: 'setLoyaltyData',
  },
  offersData: {
    endpoint: getOffersData,
    action: 'setOffersData',
  },
  offersCarouselData: {
    endpoint: getCarouselOffersData,
    action: 'setOffersCarouselData',
  },
  surveyQuestions: {
    endpoint: getSurveyData,
    action: 'setSurveyQuestions',
  },
  referralCode: {
    endpoint: getReferralCode,
    action: 'setReferralCode',
  },
  transactions: {
    endpoint: getTransactionData,
    action: 'setTransactions',
  },
};

interface OptionsProps {
  retryHandler?: () => Promise<EndpointTypes>;
}

export const useFetchFromService = (apiKey: Endpoints, options?: OptionsProps) => {
  const { isAuthenticated } = useAuth();

  const { dispatch } = useStore();
  const handleError = useErrorHandler();

  const fetchAndSetData = useCallback(async () => {
    if (isAuthenticated) {
      try {
        const { retryHandler } = options || {};
        const { action, endpoint } = services[apiKey];
        const fetchMethod = retryHandler || endpoint;

        const response = await fetchMethod();

        dispatch({
          type: action,
          payload: { [apiKey]: response },
        });
      } catch (error) {
        handleError(error);
      }
    }
  }, [apiKey, dispatch, handleError, isAuthenticated, options]);

  return fetchAndSetData;
};
